import AccountActiveIcon from '@assets/icons/account-active.svg';
import AccountInactiveIcon from '@assets/icons/account-inactive.svg';
import CatalogActiveIcon from '@assets/icons/catalog-active.svg';
import CatalogBadgedActiveIcon from '@assets/icons/catalog-badged-active.svg';
import CatalogBadgedInactiveIcon from '@assets/icons/catalog-badged-inactive.svg';
import CatalogInactiveIcon from '@assets/icons/catalog-inactive.svg';
import ChatsActiveIcon from '@assets/icons/chats-active.svg';
import ChatsBadgedActiveIcon from '@assets/icons/chats-badged-active.svg';
import ChatsBadgedInactiveIcon from '@assets/icons/chats-badged-inactive.svg';
import ChatsInactiveIcon from '@assets/icons/chats-inactive.svg';
import OrderGuideActiveIcon from '@assets/icons/order-guide-nav-active.svg';
import OrderGuideInactiveIcon from '@assets/icons/order-guide-nav-inactive.svg';
import OrdersActiveIcon from '@assets/icons/orders-active.svg';
import OrdersInactiveIcon from '@assets/icons/orders-inactive.svg';
import PaymentsActiveIcon from '@assets/icons/payments-active.svg';
import PaymentsBadgedInactiveIcon from '@assets/icons/payments-badged-inactive.svg';
import PaymentsInactiveIcon from '@assets/icons/payments-inactive.svg';
import React from 'react';
import { APP_ROUTES } from '@/navigators/routes';
import { BottomTabNavigationOptions } from '@react-navigation/bottom-tabs';
import { SvgProps } from 'react-native-svg';
import { useChatsContext } from '@/contexts/chats-context';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useSalesRepMode } from '@/hooks/use-sales-rep-mode';
import { useTheme } from '@/contexts/theme-context';
import { useVolumePromotionsContext } from '@/contexts/volume-promotions-context';

const ACTIVE_TAB_ICONS: { [key: string]: React.FC<SvgProps> } = {
  [APP_ROUTES.Account]: AccountActiveIcon,
  [APP_ROUTES.Orders]: OrdersActiveIcon,
  [APP_ROUTES.OrderGuide]: OrderGuideActiveIcon,
  [APP_ROUTES.Payments]: PaymentsActiveIcon,
  [APP_ROUTES.Restaurant]: AccountActiveIcon,
};

const INACTIVE_TAB_ICONS: { [key: string]: React.FC<SvgProps> } = {
  [APP_ROUTES.Account]: AccountInactiveIcon,
  [APP_ROUTES.Orders]: OrdersInactiveIcon,
  [APP_ROUTES.OrderGuide]: OrderGuideInactiveIcon,
  [APP_ROUTES.Payments]: PaymentsInactiveIcon,
  [APP_ROUTES.Restaurant]: AccountInactiveIcon,
};

type Props = {
  routeName: string;
} & Parameters<Exclude<BottomTabNavigationOptions['tabBarIcon'], undefined>>[0];

export const TabBarIcon: React.FC<Props> = ({ focused, routeName }) => {
  const { hasNewInvoices } = usePaymentsContext();
  const { hasNewPromotions } = useVolumePromotionsContext();
  const { hasUnreadChats } = useChatsContext();
  const theme = useTheme();
  const salesRepMode = useSalesRepMode();

  let IconComponent;
  if (routeName === APP_ROUTES.Chat) {
    if (hasUnreadChats) {
      IconComponent = focused ? ChatsBadgedActiveIcon : ChatsBadgedInactiveIcon;
    } else {
      IconComponent = focused ? ChatsActiveIcon : ChatsInactiveIcon;
    }
  } else if (routeName === APP_ROUTES.Catalog) {
    if (hasNewPromotions) {
      IconComponent = focused
        ? CatalogBadgedActiveIcon
        : CatalogBadgedInactiveIcon;
    } else {
      IconComponent = focused ? CatalogActiveIcon : CatalogInactiveIcon;
    }
  } else if (routeName === APP_ROUTES.Payments) {
    if (hasNewInvoices && !focused) {
      IconComponent = PaymentsBadgedInactiveIcon;
    } else {
      IconComponent = focused ? PaymentsActiveIcon : PaymentsInactiveIcon;
    }
  } else {
    IconComponent = focused
      ? ACTIVE_TAB_ICONS[routeName]
      : INACTIVE_TAB_ICONS[routeName];
  }

  if (!IconComponent) {
    return null;
  }

  const iconColor = salesRepMode ? theme.colors.white : theme.colors.primary;
  return (
    <IconComponent
      accentFill={theme.colors.accent}
      fill={iconColor}
      testID="tab-bar-icon"
    />
  );
};
